import { useCallback, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import { useParams } from 'react-router';
import { useTitle } from 'react-use';

import { IDecision } from 'APIPackage/DecisionModule/interfaces/IDecision';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IKnmStatus } from 'APIPackage/CatalogsModule/interfaces/IKnmStatus';
import {
  setDirtyActGuid,
  setDirtyResultDecisionGuid,
  setInitHash,
} from 'CorePackage/HeaderModule/actions/headerActions';

import { DecisionForm } from './DecisionForm';
import { IDecisionForm } from '../interfaces/IDecisionForm';
import { convertDecisionFormValuesToCreateParams } from '../common/convertDecisionFormValuesToCreateParams';
import {
  decisionCatalogsInit,
  decisionCreate,
  decisionGetHistoryChanges,
  decisionReset,
  decisionSetExternalParams,
  decisionSetStatus,
  decisionSetUrlId,
  decisionUpdate,
} from '../actions/decisionActions';
import { getDecisionData, getDecisionUpdateIsSuccess } from '../selectors/decisionSelectors';
import { formatDateLocale, getFormatedClientTime } from '../../../CorePackage/MainModule/common/formatDate';
import {
  DATE_FORMAT_ISO_FNS,
  DATE_FORMAT_LONG_MINUTE_FNS,
} from '../../../CorePackage/MainModule/constants/dateFormats';

interface IDecisionStoreProps {
  decisionRawData: IDecision | undefined;
  isDecisionUpdateSuccess: boolean;
}

const DecisionWithoutStore = ({ decisionRawData, isDecisionUpdateSuccess }: IDecisionStoreProps) => {
  const [title, setTitle] = useState('Решение');

  useTitle(title);

  const dispatch = useDispatch<Dispatch<RootActions>>();
  const initCatalogs = useCallback(() => dispatch(decisionCatalogsInit()), [dispatch]);
  const resetKnmStore = useCallback(() => dispatch(decisionReset()), [dispatch]);
  const setDecisionUrlId = useCallback((id: string) => dispatch(decisionSetUrlId(id)), [dispatch]);

  const { id: decisionRouteId } = useParams<{ id: string }>();
  const [canSubmit, setCanSubmit] = useState(true);
  const decisionStatus = decisionRawData?.status;

  const setStatus = useCallback((statusValue: IKnmStatus | undefined) => dispatch(decisionSetStatus(statusValue)), [
    dispatch,
  ]);

  useEffect(() => {
    if (decisionStatus) {
      setStatus(decisionStatus);
    }
  }, [decisionStatus, setStatus]);

  useEffect(() => {
    if (window.location.hash) {
      dispatch(setInitHash(window.location.hash));
    }
    if (window.location.search) {
      const urlParams = new URLSearchParams(window.location.search);
      const externalParams = Object.fromEntries(urlParams);

      if (externalParams) {
        dispatch(decisionSetExternalParams(externalParams));
      }
    }
  }, []);

  useEffect(() => {
    initCatalogs();
    dispatch(setDirtyActGuid(undefined));
    dispatch(setDirtyResultDecisionGuid(undefined));

    return () => {
      resetKnmStore();
    };
  }, []);

  useEffect(() => {
    if (decisionRouteId) {
      setDecisionUrlId(decisionRouteId);
    }
  }, [decisionRouteId, setDecisionUrlId]);

  useEffect(() => {
    if (isDecisionUpdateSuccess) {
      dispatch(decisionGetHistoryChanges());
    }
  }, [dispatch, isDecisionUpdateSuccess]);

  useEffect(() => {
    const decisionName = decisionRawData && decisionRawData.number ? `Решение №${decisionRawData.number}` : 'Решение';
    const decisionDate = `от ${
      decisionRawData?.createdAt
        ? formatDateLocale({
            date: decisionRawData?.createdAt,
            dateFormat: DATE_FORMAT_LONG_MINUTE_FNS,
            parseFormat: DATE_FORMAT_ISO_FNS,
          })
        : getFormatedClientTime(DATE_FORMAT_LONG_MINUTE_FNS)
    }`;
    const newTitle = `${decisionName} ${decisionDate}`;

    setTitle(newTitle);
  }, [decisionRawData]);

  function handleFormSubmit(values: IDecisionForm) {
    if (canSubmit) {
      const decisionRequestValues = convertDecisionFormValuesToCreateParams(values, decisionRawData, false);

      if (!decisionRawData?.number) {
        dispatch(decisionCreate(decisionRequestValues));
      } else {
        dispatch(decisionUpdate(decisionRequestValues));
      }
    }
  }

  return <DecisionForm setCanSubmit={setCanSubmit} handleSubmit={handleFormSubmit} />;
};

const mapStateToProps = (state: IRootState): IDecisionStoreProps => ({
  decisionRawData: getDecisionData(state),
  isDecisionUpdateSuccess: getDecisionUpdateIsSuccess(state),
});

export const Decision = connect(mapStateToProps)(DecisionWithoutStore);
